// bluaIQ — Sidebar placeholder for modules that exist as a workflow
// concept (sidebar entry + route) but whose data layer / CRUD will
// land in a focused follow-up PR pair.
//
// Used by the 7-group sidebar regrouping (PR — Sidebar 7-group
// builder workflow) for the four new entries:
//   - Project Packs / Diseños
//   - Specifications / Especificaciones
//   - Permits & Local Review / Permisos y Revisión Local
//   - Client Approvals / Aprobaciones del Cliente
//
// Purely presentational. No fetch, no useQuery, no useMutation, no
// state. The component just renders an honest, bilingual "próximamente"
// surface with a short product-purpose blurb and a stable testId for
// downstream smoke tests.
//
// IMPORTANT: this component lives at the website layer ONLY. It does
// not import TAKEOFF internals, aiAdapter, claudeService, or any
// backend. It is a pure shell render so users land on a clear empty
// state instead of a 404 when they click one of the new sidebar
// entries.

import { useTranslation } from 'react-i18next'

export type ComingSoonPlaceholderProps = {
  /**
   * Stable test id used by appNavigation.smoke + future module-level
   * smoke tests. Pattern: `coming-soon-<route-slug>` (e.g.
   * `coming-soon-project-packs`).
   */
  testId: string
  /** English module label (shown as page title in EN). */
  titleEn: string
  /** Spanish module label (shown as page title in ES). */
  titleEs: string
  /** Short EN description of what this module will do once it ships. */
  descriptionEn: string
  /** Short ES description of what this module will do once it ships. */
  descriptionEs: string
}

export default function ComingSoonPlaceholder({
  testId,
  titleEn,
  titleEs,
  descriptionEn,
  descriptionEs,
}: ComingSoonPlaceholderProps) {
  const { i18n } = useTranslation()
  const isEs = i18n.language?.startsWith('es')

  return (
    <div
      data-testid={testId}
      className="p-6 max-w-2xl mx-auto"
    >
      <div className="rounded-2xl border border-[#ECEAE4] bg-white p-8 text-center shadow-sm">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-[#F2F0EB] px-3 py-1 text-[10px] font-[800] uppercase tracking-[0.6px] text-[#9E9890]"
          data-testid={`${testId}-badge`}
        >
          {isEs ? 'Próximamente' : 'Coming soon'}
        </div>
        <h2
          className="mt-4 text-[18px] font-[800] tracking-[-0.2px] text-[#1C1A17]"
          data-testid={`${testId}-title`}
        >
          {isEs ? titleEs : titleEn}
        </h2>
        <p
          className="mx-auto mt-3 max-w-md text-[13px] leading-relaxed text-[#5E5A52]"
          data-testid={`${testId}-description`}
        >
          {isEs ? descriptionEs : descriptionEn}
        </p>
      </div>
    </div>
  )
}
